import React, { useState, useEffect, useCallback } from 'react';
import './common.css';
import Header from '../components/Header';
import Footer from '../components/Footer';

/* ─────────────────────── Utilities ─────────────────────────────── */
const rand = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

const shuffle = arr => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = rand(0, i);
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const getAgeGroup = age =>
  age <= 7 ? '5-7' : age <= 10 ? '8-10' : age <= 13 ? '11-13' : '14-18';

/* number cap per age + complexity */
const RANGES = {
  '5-7':   { easy: 10,  medium: 20,  hard: 30   },
  '8-10':  { easy: 20,  medium: 50,  hard: 100  },
  '11-13': { easy: 50,  medium: 100, hard: 200  },
  '14-18': { easy: 100, medium: 500, hard: 1000 },
};

/* 4 choices including correct, near the correct value */
const distractors = (correct, max, n = 3) => {
  const span = Math.max(5, Math.ceil((max || 20) * 0.3));
  const pool = new Set();
  let attempts = 0;
  while (pool.size < n && attempts < 200) {
    attempts++;
    const v = rand(Math.max(0, correct - span), correct + span);
    if (v !== correct && v >= 0) pool.add(v);
  }
  let f = 1;
  while (pool.size < n) { if (f !== correct) pool.add(f); f++; }
  return shuffle([correct, ...pool]);
};

const ROUNDS = 8;

/* ─────────────────────── Activity catalogue ─────────────────────── */
const ACTIVITIES = [
  { id: 'count',   name: 'Count & Click',     icon: '🔢', color: '#A8D5E2', desc: 'Count the dots and pick the right number',            minAge: 5 },
  { id: 'add',     name: 'Addition Blast',    icon: '➕', color: '#B5EAD7', desc: 'Solve addition problems at your own pace',             minAge: 5 },
  { id: 'sub',     name: 'Subtraction Stars', icon: '⭐', color: '#FFD3B6', desc: 'Master subtraction step by step',                     minAge: 6 },
  { id: 'mul',     name: 'Times Tables',      icon: '✖️', color: '#E2A8D5', desc: 'Practice your multiplication tables',                 minAge: 8 },
  { id: 'div',     name: 'Division Quest',    icon: '➗', color: '#F9B5AC', desc: 'Explore the world of division',                       minAge: 9 },
  { id: 'pattern', name: 'Number Patterns',   icon: '🔗', color: '#C7CEEA', desc: 'Find the missing number in the sequence',             minAge: 6 },
  { id: 'bonds',   name: 'Number Bonds',      icon: '💞', color: '#FFDAC1', desc: 'Complete the bond to reach the target number',        minAge: 5 },
  { id: 'shapes',  name: 'Shape Explorer',    icon: '🔷', color: '#A8D5E2', desc: 'Identify 2D shapes by their appearance',              minAge: 5 },
  { id: 'frac',    name: 'Fraction Fun',      icon: '🧁', color: '#B5EAD7', desc: 'See and identify fractions visually',                 minAge: 8 },
  { id: 'blitz',   name: 'Mental Math Blitz', icon: '⚡', color: '#E2A8D5', desc: 'Speed round — answer as many as you can in 30 s!',   minAge: 7 },
  { id: 'place',   name: 'Place Value Park',  icon: '🏙️', color: '#FFD3B6', desc: 'Identify hundreds, tens, and ones digits',           minAge: 8 },
  { id: 'oddeven', name: 'Odd or Even',       icon: '🔄', color: '#F9B5AC', desc: 'Tap all the odd or even numbers in the set',         minAge: 5 },
];

/* ─────────────────────── Main page ──────────────────────────────── */
const MathActivities = ({ onNavigate }) => {
  const [stage, setStage] = useState('setup');
  const [cfg,   setCfg]   = useState(null);
  const [act,   setAct]   = useState(null);

  return (
    <div className="home-container">
      <Header onNavigate={onNavigate} showHomeButton />
      <div className="home-container">
        <div className="hero-section" style={{ padding: '40px 20px 90px' }}>
          <h1 className="hero-title">🔢 Math Activities</h1>
          <p className="hero-description">
            Explore the world of numbers! Choose your settings, pick an activity, and practise at your own pace.
          </p>
        </div>

        {stage === 'setup'  && (
          <SetupScreen onStart={c => { setCfg(c); setStage('picker'); }} />
        )}
        {stage === 'picker' && (
          <ActivityPicker
            cfg={cfg}
            onSelect={a => { setAct(a); setStage('game'); }}
            onBack={() => setStage('setup')}
          />
        )}
        {stage === 'game' && act && (
          <GameRouter
            actId={act.id}
            cfg={cfg}
            onBack={() => { setAct(null); setStage('picker'); }}
          />
        )}
      </div>
      <Footer />
    </div>
  );
};

/* ─────────────────────── Setup Screen ──────────────────────────── */
const SetupScreen = ({ onStart }) => {
  const [age,        setAge]  = useState(8);
  const [complexity, setCmpl] = useState('easy');
  const ag  = getAgeGroup(age);
  const max = RANGES[ag][complexity];

  return (
    <div className="quiz-card sf-setup">
      <h2 className="section-title">⚙️ Set Up Your Session</h2>
      <div className="sf-setup-form">
        <div className="sf-setup-field">
          <label htmlFor="math-age">🎂 Your Age</label>
          <div className="sf-slider-row">
            <input
              id="math-age" type="range" min={5} max={18} value={age}
              onChange={e => setAge(+e.target.value)} className="sf-slider"
            />
            <span className="sf-slider-value">{age}</span>
          </div>
          <span className="sf-age-group-label">Age group: {ag} · Numbers up to {max}</span>
        </div>

        <div className="sf-setup-field">
          <label htmlFor="math-cmpl">📊 Complexity</label>
          <select id="math-cmpl" className="form-select" value={complexity}
            onChange={e => setCmpl(e.target.value)}>
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
          </select>
        </div>
      </div>
      <button
        className="start-btn"
        style={{ marginTop: '30px', fontSize: '1.5rem', padding: '18px 50px' }}
        onClick={() => onStart({ age, ageGroup: ag, complexity, max })}
      >
        🚀 Choose an Activity
      </button>
    </div>
  );
};

/* ─────────────────────── Activity Picker ─────────────────────── */
const ActivityPicker = ({ cfg, onSelect, onBack }) => (
  <div style={{ marginTop: '20px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
      <h2 className="section-title" style={{ margin: 0 }}>🎮 Pick an Activity</h2>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        <span className="sf-age-group-label">Age {cfg.ageGroup} · {cfg.complexity} · up to {cfg.max}</span>
        <button className="help-btn" onClick={onBack}>⚙️ Change Settings</button>
      </div>
    </div>
    <div className="math-activity-grid">
      {ACTIVITIES.filter(a => cfg.age >= a.minAge).map(a => (
        <div
          key={a.id}
          className="math-activity-tile"
          style={{ '--tile-color': a.color }}
          onClick={() => onSelect(a)}
        >
          <div className="math-tile-icon">{a.icon}</div>
          <h3 className="math-tile-name">{a.name}</h3>
          <p className="math-tile-desc">{a.desc}</p>
          <span className="math-tile-play">Play →</span>
        </div>
      ))}
    </div>
  </div>
);

/* ─────────────────────── Game Router ────────────────────────── */
const GameRouter = ({ actId, cfg, onBack }) => {
  const p = { cfg, onBack };
  switch (actId) {
    case 'count':   return <CountGame   {...p} />;
    case 'add':     return <MCQGame title="➕ Addition Blast"     hint="Add the two numbers!"                gen={genAdd}     {...p} />;
    case 'sub':     return <MCQGame title="⭐ Subtraction Stars"   hint="Subtract the second from the first!" gen={genSub}     {...p} />;
    case 'mul':     return <MCQGame title="✖️ Times Tables"       hint="Multiply the two numbers!"           gen={genMul}     {...p} />;
    case 'div':     return <MCQGame title="➗ Division Quest"      hint="Divide the first by the second!"     gen={genDiv}     {...p} />;
    case 'pattern': return <MCQGame title="🔗 Number Patterns"    hint="What is the missing number?"         gen={genPattern} {...p} />;
    case 'bonds':   return <MCQGame title="💞 Number Bonds"       hint="Complete the bond to reach the target!" gen={genBonds} {...p} />;
    case 'shapes':  return <ShapesGame    {...p} />;
    case 'frac':    return <FractionsGame {...p} />;
    case 'blitz':   return <BlitzGame     {...p} />;
    case 'place':   return <MCQGame title="🏙️ Place Value Park"   hint="Which digit is in that position?"    gen={genPlace}   {...p} />;
    case 'oddeven': return <OddEvenGame   {...p} />;
    default:        return null;
  }
};

/* ─────────────────────── Question generators ───────────────── */
const genAdd = cfg => {
  const a = rand(1, cfg.max), b = rand(1, cfg.max);
  const correct = a + b;
  return { q: `${a} + ${b} = ?`, correct, choices: distractors(correct, cfg.max * 2) };
};

const genSub = cfg => {
  const a = rand(1, cfg.max), b = rand(0, a);
  const correct = a - b;
  return { q: `${a} − ${b} = ?`, correct, choices: distractors(correct, a) };
};

const genMul = cfg => {
  const tMax = cfg.max <= 20 ? 5 : cfg.max <= 50 ? 10 : 12;
  const a = rand(1, tMax), b = rand(1, tMax);
  const correct = a * b;
  return { q: `${a} × ${b} = ?`, correct, choices: distractors(correct, tMax * tMax) };
};

const genDiv = cfg => {
  const tMax = cfg.max <= 20 ? 5 : cfg.max <= 50 ? 10 : 12;
  const b = rand(2, tMax), result = rand(1, tMax);
  const a = b * result;
  return { q: `${a} ÷ ${b} = ?`, correct: result, choices: distractors(result, tMax) };
};

const genPattern = cfg => {
  const step  = rand(1, Math.max(2, Math.ceil(cfg.max / 10)));
  const start = rand(1, Math.max(1, cfg.max - step * 5));
  const seq   = [0, 1, 2, 3, 4].map(i => start + i * step);
  const blankIdx = rand(1, 3);
  const correct  = seq[blankIdx];
  const display  = seq.map((n, i) => i === blankIdx ? ' ? ' : n).join(' , ');
  return { q: display, correct, choices: distractors(correct, cfg.max) };
};

const genBonds = cfg => {
  const target  = rand(Math.ceil(cfg.max * 0.2), Math.ceil(cfg.max * 0.5));
  const given   = rand(0, target - 1);
  const correct = target - given;
  return { q: `${given} + ? = ${target}`, correct, choices: distractors(correct, target) };
};

const genPlace = cfg => {
  const num    = rand(100, Math.max(110, cfg.max));
  const places = ['ones', 'tens', 'hundreds'];
  const pos    = places[rand(0, places.length - 1)];
  const val    = { ones: num % 10, tens: Math.floor(num / 10) % 10, hundreds: Math.floor(num / 100) % 10 };
  const correct = val[pos];
  const choices = distractors(correct, 9).map(v => Math.max(0, Math.min(9, v)));
  return { q: `What is the ${pos} digit of  ${num}?`, correct, choices };
};

/* ─────────────────────── Generic MCQ Game ──────────────────── */
const MCQGame = ({ title, hint, gen, cfg, onBack }) => {
  const next = useCallback(() => gen(cfg), [gen, cfg]);
  const [round,    setRound]  = useState(1);
  const [score,    setScore]  = useState(0);
  const [done,     setDone]   = useState(false);
  const [q,        setQ]      = useState(next);
  const [selected, setSel]    = useState(null);

  const handleChoice = c => { if (selected !== null) return; setSel(c); };
  const handleNext   = () => {
    const ok = selected === q.correct, ns = score + (ok ? 1 : 0);
    if (round >= ROUNDS) { setScore(ns); setDone(true); }
    else { setScore(ns); setRound(r => r + 1); setQ(next()); setSel(null); }
  };
  const replay = () => { setRound(1); setScore(0); setDone(false); setQ(next()); setSel(null); };

  if (done) return <Results score={score} total={ROUNDS} onBack={onBack} onReplay={replay} />;

  const answered = selected !== null;
  return (
    <div className="quiz-card">
      <div className="sf-meta">
        <span className="sf-progress">{title} · Q{round}/{ROUNDS}</span>
        <span className="sf-score">⭐ {score}</span>
        <button className="help-btn" onClick={onBack} style={{ fontSize: '1rem', padding: '6px 14px' }}>← Back</button>
      </div>
      {hint && <p className="sf-hint">{hint}</p>}
      <div className="math-question-box">{q.q}</div>
      <div className="math-choices">
        {q.choices.map((c, i) => {
          let cls = 'math-choice-btn';
          if (answered) {
            if (c === q.correct) cls += ' math-choice-correct';
            else if (c === selected) cls += ' math-choice-wrong';
          }
          return <button key={i} className={cls} onClick={() => handleChoice(c)}>{c}</button>;
        })}
      </div>
      {answered && (
        <>
          <div className={`feedback ${selected === q.correct ? 'correct' : 'incorrect'}`}>
            {selected === q.correct ? '✅ Correct! Great job!' : `❌ The answer was ${q.correct}`}
          </div>
          <div className="sf-actions">
            <button className="start-btn" onClick={handleNext}>
              {round >= ROUNDS ? '🎉 Finish' : '➡️ Next'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

/* ─────────────────────── COUNT & CLICK ─────────────────────── */
const CountGame = ({ cfg, onBack }) => {
  const dotMax = Math.min(cfg.max, 20);
  const genQ   = useCallback(() => {
    const n = rand(1, dotMax);
    return { n, choices: distractors(n, dotMax) };
  }, [dotMax]);

  const [round, setRound] = useState(1);
  const [score, setScore] = useState(0);
  const [done,  setDone]  = useState(false);
  const [q,     setQ]     = useState(genQ);
  const [sel,   setSel]   = useState(null);

  const handleChoice = c => { if (sel !== null) return; setSel(c); };
  const handleNext   = () => {
    const ok = sel === q.n, ns = score + (ok ? 1 : 0);
    if (round >= ROUNDS) { setScore(ns); setDone(true); }
    else { setScore(ns); setRound(r => r + 1); setQ(genQ()); setSel(null); }
  };
  const replay = () => { setRound(1); setScore(0); setDone(false); setQ(genQ()); setSel(null); };

  if (done) return <Results score={score} total={ROUNDS} onBack={onBack} onReplay={replay} />;

  const answered = sel !== null;
  return (
    <div className="quiz-card">
      <div className="sf-meta">
        <span className="sf-progress">🔢 Count & Click · Q{round}/{ROUNDS}</span>
        <span className="sf-score">⭐ {score}</span>
        <button className="help-btn" onClick={onBack} style={{ fontSize: '1rem', padding: '6px 14px' }}>← Back</button>
      </div>
      <p className="sf-hint">How many dots do you see? 🔢</p>
      <div className="math-dot-grid">
        {Array.from({ length: q.n }).map((_, i) => <span key={i} className="math-dot" />)}
      </div>
      <div className="math-choices">
        {q.choices.map((c, i) => {
          let cls = 'math-choice-btn';
          if (answered) {
            if (c === q.n) cls += ' math-choice-correct';
            else if (c === sel) cls += ' math-choice-wrong';
          }
          return <button key={i} className={cls} onClick={() => handleChoice(c)}>{c}</button>;
        })}
      </div>
      {answered && (
        <>
          <div className={`feedback ${sel === q.n ? 'correct' : 'incorrect'}`}>
            {sel === q.n ? '✅ Correct! Great job!' : `❌ There were ${q.n} dots`}
          </div>
          <div className="sf-actions">
            <button className="start-btn" onClick={handleNext}>
              {round >= ROUNDS ? '🎉 Finish' : '➡️ Next'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

/* ─────────────────────── SHAPE EXPLORER ────────────────────── */
const SHAPE_DEFS = [
  { name: 'Circle',    css: { width: '100px', height: '100px', borderRadius: '50%',                                                                     background: '#A8D5E2' } },
  { name: 'Square',    css: { width: '100px', height: '100px',                                                                                           background: '#B5EAD7' } },
  { name: 'Rectangle', css: { width: '150px', height: '80px',                                                                                            background: '#FFD3B6' } },
  { name: 'Triangle',  css: { width: 0, height: 0, borderLeft: '55px solid transparent', borderRight: '55px solid transparent', borderBottom: '110px solid #E2A8D5', background: 'none' } },
  { name: 'Pentagon',  css: { width: '100px', height: '100px', background: '#C7CEEA', clipPath: 'polygon(50% 0%,100% 38%,82% 100%,18% 100%,0% 38%)' } },
  { name: 'Hexagon',   css: { width: '110px', height: '110px', background: '#F9B5AC', clipPath: 'polygon(25% 0%,75% 0%,100% 50%,75% 100%,25% 100%,0% 50%)' } },
  { name: 'Oval',      css: { width: '140px', height: '80px',  borderRadius: '50%',                                                                     background: '#FFDAC1' } },
  { name: 'Diamond',   css: { width: '90px',  height: '90px',  background: '#A8D5E2', clipPath: 'polygon(50% 0%,100% 50%,50% 100%,0% 50%)' } },
];

const ShapesGame = ({ cfg, onBack }) => {
  const genQ = useCallback(() => {
    const correct = SHAPE_DEFS[rand(0, SHAPE_DEFS.length - 1)];
    const wrongs  = shuffle(SHAPE_DEFS.filter(s => s.name !== correct.name)).slice(0, 3);
    return { correct, choices: shuffle([correct, ...wrongs].map(s => s.name)) };
  }, []);

  const [round, setRound] = useState(1);
  const [score, setScore] = useState(0);
  const [done,  setDone]  = useState(false);
  const [q,     setQ]     = useState(genQ);
  const [sel,   setSel]   = useState(null);

  const handleChoice = n => { if (sel !== null) return; setSel(n); };
  const handleNext   = () => {
    const ok = sel === q.correct.name, ns = score + (ok ? 1 : 0);
    if (round >= ROUNDS) { setScore(ns); setDone(true); }
    else { setScore(ns); setRound(r => r + 1); setQ(genQ()); setSel(null); }
  };
  const replay = () => { setRound(1); setScore(0); setDone(false); setQ(genQ()); setSel(null); };

  if (done) return <Results score={score} total={ROUNDS} onBack={onBack} onReplay={replay} />;

  const answered = sel !== null;
  return (
    <div className="quiz-card">
      <div className="sf-meta">
        <span className="sf-progress">🔷 Shape Explorer · Q{round}/{ROUNDS}</span>
        <span className="sf-score">⭐ {score}</span>
        <button className="help-btn" onClick={onBack} style={{ fontSize: '1rem', padding: '6px 14px' }}>← Back</button>
      </div>
      <p className="sf-hint">What shape is this? 🔷</p>
      <div className="math-shape-display">
        <div style={q.correct.css} />
      </div>
      <div className="math-choices">
        {q.choices.map((c, i) => {
          let cls = 'math-choice-btn';
          if (answered) {
            if (c === q.correct.name) cls += ' math-choice-correct';
            else if (c === sel) cls += ' math-choice-wrong';
          }
          return <button key={i} className={cls} onClick={() => handleChoice(c)}>{c}</button>;
        })}
      </div>
      {answered && (
        <>
          <div className={`feedback ${sel === q.correct.name ? 'correct' : 'incorrect'}`}>
            {sel === q.correct.name ? '✅ Correct! Great job!' : `❌ It was a ${q.correct.name}`}
          </div>
          <div className="sf-actions">
            <button className="start-btn" onClick={handleNext}>
              {round >= ROUNDS ? '🎉 Finish' : '➡️ Next'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

/* ─────────────────────── FRACTION FUN ──────────────────────── */
const FRACTIONS = [
  { num: 1, den: 2 }, { num: 1, den: 3 }, { num: 2, den: 3 },
  { num: 1, den: 4 }, { num: 3, den: 4 }, { num: 1, den: 5 },
  { num: 2, den: 5 }, { num: 3, den: 5 }, { num: 4, den: 5 },
  { num: 1, den: 6 }, { num: 5, den: 6 },
];
const fracLabel = f => `${f.num}/${f.den}`;

const FractionsGame = ({ cfg, onBack }) => {
  const genQ = useCallback(() => {
    const correct = FRACTIONS[rand(0, FRACTIONS.length - 1)];
    const wrongs  = shuffle(FRACTIONS.filter(f => fracLabel(f) !== fracLabel(correct))).slice(0, 3);
    return { correct, choices: shuffle([correct, ...wrongs].map(fracLabel)) };
  }, []);

  const [round, setRound] = useState(1);
  const [score, setScore] = useState(0);
  const [done,  setDone]  = useState(false);
  const [q,     setQ]     = useState(genQ);
  const [sel,   setSel]   = useState(null);

  const handleChoice = c => { if (sel !== null) return; setSel(c); };
  const handleNext   = () => {
    const ok = sel === fracLabel(q.correct), ns = score + (ok ? 1 : 0);
    if (round >= ROUNDS) { setScore(ns); setDone(true); }
    else { setScore(ns); setRound(r => r + 1); setQ(genQ()); setSel(null); }
  };
  const replay = () => { setRound(1); setScore(0); setDone(false); setQ(genQ()); setSel(null); };

  if (done) return <Results score={score} total={ROUNDS} onBack={onBack} onReplay={replay} />;

  const answered = sel !== null;
  const { num, den } = q.correct;
  return (
    <div className="quiz-card">
      <div className="sf-meta">
        <span className="sf-progress">🧁 Fraction Fun · Q{round}/{ROUNDS}</span>
        <span className="sf-score">⭐ {score}</span>
        <button className="help-btn" onClick={onBack} style={{ fontSize: '1rem', padding: '6px 14px' }}>← Back</button>
      </div>
      <p className="sf-hint">What fraction is shaded? 🧁</p>
      <div className="math-fraction-visual">
        {Array.from({ length: den }).map((_, i) => (
          <div key={i} className={`math-fraction-box${i < num ? ' shaded' : ''}`} />
        ))}
      </div>
      <div className="math-choices">
        {q.choices.map((c, i) => {
          let cls = 'math-choice-btn';
          if (answered) {
            if (c === fracLabel(q.correct)) cls += ' math-choice-correct';
            else if (c === sel) cls += ' math-choice-wrong';
          }
          return <button key={i} className={cls} onClick={() => handleChoice(c)}>{c}</button>;
        })}
      </div>
      {answered && (
        <>
          <div className={`feedback ${sel === fracLabel(q.correct) ? 'correct' : 'incorrect'}`}>
            {sel === fracLabel(q.correct) ? '✅ Correct! Great job!' : `❌ It was ${fracLabel(q.correct)}`}
          </div>
          <div className="sf-actions">
            <button className="start-btn" onClick={handleNext}>
              {round >= ROUNDS ? '🎉 Finish' : '➡️ Next'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

/* ─────────────────────── MENTAL MATH BLITZ ─────────────────── */
const TIME_LIMIT = 30;

const BlitzGame = ({ cfg, onBack }) => {
  const genQ = useCallback(() => {
    const ops = cfg.max <= 20 ? ['+'] : cfg.max <= 50 ? ['+', '-'] : ['+', '-', '×'];
    const op = ops[rand(0, ops.length - 1)];
    let a, b, correct, display;
    if (op === '+') {
      a = rand(1, Math.ceil(cfg.max * 0.5));
      b = rand(1, Math.ceil(cfg.max * 0.5));
      correct = a + b; display = `${a} + ${b}`;
    } else if (op === '-') {
      a = rand(1, cfg.max); b = rand(0, a);
      correct = a - b; display = `${a} − ${b}`;
    } else {
      a = rand(1, 10); b = rand(1, 10);
      correct = a * b; display = `${a} × ${b}`;
    }
    return { display, correct };
  }, [cfg.max]);

  const [timeLeft, setTimeLeft] = useState(TIME_LIMIT);
  const [q,        setQ]        = useState(genQ);
  const [input,    setInput]    = useState('');
  const [score,    setScore]    = useState(0);
  const [count,    setCount]    = useState(0);
  const [done,     setDone]     = useState(false);
  const [flash,    setFlash]    = useState(null);

  useEffect(() => {
    if (done) return;
    const t = setInterval(() => {
      setTimeLeft(s => { if (s <= 1) { clearInterval(t); setDone(true); return 0; } return s - 1; });
    }, 1000);
    return () => clearInterval(t);
  }, [done]);

  const handleSubmit = () => {
    if (input === '') return;
    const ok = parseInt(input, 10) === q.correct;
    setScore(s => s + (ok ? 1 : 0));
    setCount(c => c + 1);
    setFlash(ok ? 'ok' : 'bad');
    setTimeout(() => { setFlash(null); setQ(genQ()); setInput(''); }, 600);
  };
  const replay = () => {
    setTimeLeft(TIME_LIMIT); setQ(genQ()); setInput('');
    setScore(0); setCount(0); setDone(false); setFlash(null);
  };

  if (done) return (
    <div className="quiz-card" style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '5rem', marginBottom: '20px' }}>⚡</div>
      <h2 className="section-title">Time's up!</h2>
      <p style={{ fontSize: '1.8rem', color: '#667eea', fontWeight: 'bold', margin: '20px 0' }}>
        {score} correct out of {count} answered
      </p>
      <p style={{ fontSize: '1.4rem', color: '#34495E' }}>
        {score === count && count > 0 ? "🏆 Perfect! You're a math star!"
          : score >= count * 0.7     ? '🌟 Great speed! Keep practising!'
          : '💪 Good effort! Try again to beat your score!'}
      </p>
      <div className="sf-actions" style={{ marginTop: '20px' }}>
        <button className="start-btn" onClick={replay}>🔄 Play Again</button>
        <button className="help-btn" onClick={onBack}>← Back</button>
      </div>
    </div>
  );

  return (
    <div className={`quiz-card${flash ? ` math-blitz-${flash}` : ''}`}>
      <div className="sf-meta">
        <span className="sf-progress">⚡ Mental Math Blitz</span>
        <span className="sf-score">✅ {score}</span>
        <button className="help-btn" onClick={onBack} style={{ fontSize: '1rem', padding: '6px 14px' }}>← Back</button>
      </div>
      <div className="math-blitz-timer" style={{ color: timeLeft <= 10 ? '#c0392b' : '#667eea' }}>
        ⏱ {timeLeft}s
      </div>
      <div className="math-question-box" style={{ fontSize: '3rem' }}>{q.display} = ?</div>
      <input
        className="math-blitz-input"
        type="number"
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }}
        placeholder="Your answer…"
        autoFocus
      />
      <button
        className="start-btn"
        onClick={handleSubmit}
        disabled={input === ''}
        style={{ marginTop: '16px', fontSize: '1.3rem', padding: '14px 40px' }}
      >
        ✔ Submit
      </button>
    </div>
  );
};

/* ─────────────────────── ODD OR EVEN ───────────────────────── */
const OddEvenGame = ({ cfg, onBack }) => {
  const NUM_COUNT = 6;
  const genQ = useCallback(() => {
    const nums   = Array.from({ length: NUM_COUNT }, () => rand(1, Math.min(cfg.max, 50)));
    const target = rand(0, 1) === 0 ? 'odd' : 'even';
    return { nums, target };
  }, [cfg.max]);

  const [round,   setRound]   = useState(1);
  const [score,   setScore]   = useState(0);
  const [done,    setDone]    = useState(false);
  const [q,       setQ]       = useState(genQ);
  const [sel,     setSel]     = useState([]);
  const [checked, setChecked] = useState(false);

  const toggle = n => {
    if (checked) return;
    setSel(s => s.includes(n) ? s.filter(x => x !== n) : [...s, n]);
  };

  const correctNums = q.nums.filter(n => q.target === 'odd' ? n % 2 !== 0 : n % 2 === 0);

  const handleCheck = () => {
    const ok = correctNums.length === sel.length && correctNums.every(n => sel.includes(n));
    setChecked(true);
    if (ok) setScore(s => s + 1);
  };

  const handleNext = () => {
    if (round >= ROUNDS) setDone(true);
    else { setRound(r => r + 1); setQ(genQ()); setSel([]); setChecked(false); }
  };
  const replay = () => { setRound(1); setScore(0); setDone(false); setQ(genQ()); setSel([]); setChecked(false); };

  if (done) return <Results score={score} total={ROUNDS} onBack={onBack} onReplay={replay} />;

  const allCorrect = correctNums.length === sel.length && correctNums.every(n => sel.includes(n));
  return (
    <div className="quiz-card">
      <div className="sf-meta">
        <span className="sf-progress">🔄 Odd or Even · Q{round}/{ROUNDS}</span>
        <span className="sf-score">⭐ {score}</span>
        <button className="help-btn" onClick={onBack} style={{ fontSize: '1rem', padding: '6px 14px' }}>← Back</button>
      </div>
      <p className="sf-hint">Tap all the <strong>{q.target}</strong> numbers! 🔄</p>
      <div className="math-oddeven-grid">
        {q.nums.map((n, i) => {
          let cls = 'math-oddeven-num';
          if (checked) {
            if (correctNums.includes(n)) cls += ' oe-correct';
            else if (sel.includes(n))    cls += ' oe-wrong';
          } else if (sel.includes(n)) {
            cls += ' oe-selected';
          }
          return <button key={i} className={cls} onClick={() => toggle(n)}>{n}</button>;
        })}
      </div>
      {!checked && (
        <div className="sf-actions">
          <button className="check-btn" onClick={handleCheck} disabled={sel.length === 0}>
            ✔️ Check
          </button>
        </div>
      )}
      {checked && (
        <>
          <div className={`feedback ${allCorrect ? 'correct' : 'incorrect'}`}>
            {allCorrect ? '✅ All correct!' : `❌ The ${q.target} numbers were: ${correctNums.join(', ')}`}
          </div>
          <div className="sf-actions">
            <button className="start-btn" onClick={handleNext}>
              {round >= ROUNDS ? '🎉 Finish' : '➡️ Next'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

/* ─────────────────────── Results ────────────────────────────── */
const Results = ({ score, total, onBack, onReplay }) => (
  <div className="quiz-card" style={{ textAlign: 'center' }}>
    <div style={{ fontSize: '5rem', marginBottom: '20px' }}>🎉</div>
    <h2 className="section-title">Well done!</h2>
    <p style={{ fontSize: '1.8rem', color: '#667eea', fontWeight: 'bold', margin: '20px 0' }}>
      Score: {score} / {total}
    </p>
    <p style={{ fontSize: '1.4rem', color: '#34495E' }}>
      {score === total   ? "🏆 Perfect score! You're amazing!"
        : score >= total * 0.7 ? '🌟 Great job! Keep practising!'
        : '💪 Good effort! Try again to improve!'}
    </p>
    <div className="sf-actions" style={{ marginTop: '20px' }}>
      <button className="start-btn" onClick={onReplay}>🔄 Play Again</button>
      <button className="help-btn"  onClick={onBack}>← Back to Activities</button>
    </div>
  </div>
);

export default MathActivities;
